import React, {
  createContext,
  useState,
  useLayoutEffect,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import { EVALUATION_DEFAULT_PAGINATION_PAGE_SIZE } from './peerReviewHelpers.js';
import { useUser } from '../../contexts/userContext.js';
import {
  getAssignedPeerReviews,
  saveScores,
} from '../../services/resultsService.js';
import { getScoredServiceTypePrograms } from '../../services/programService.js';
import {
  appendProgramsToResult,
  getTeamScoresToSave,
  mapEvaluationsForSaving,
  updateEvaluationProgram,
} from './peerReviewHelpers.js';
import { ToastComponent } from '@tylertech/forge';
import { messageManager } from '../System/messageManager/messageManager.js';
import { LoaderContext } from '../../contexts/loaderContext.js';
import { useNameConventions } from '../../contexts/nameConventionsContext.js';
import { Context } from '../../store/Store.js';
import { REDUCER_ACTION_TYPES } from '../../store/Reducer.js';

const peerReviewInitialState = {
  activeStep: null,
  setActiveStep: () => {},
  cardSelected: null,
  setCardSelected: () => {},
  emptyState: true,
  setEmptyState: () => {},
  resultsValidationStatus: null,
  evaluationPrograms: [],
  evaluationResults: [],
  assignedReviews: [],
  openPanel: false,
  setOpenPanel: () => {},
};

const PeerReviewContext = createContext(peerReviewInitialState);

const PeerReviewContextProvider = ({ children }) => {
  const [departmentReconciliation, setDepartmentReconciliation] = useState(false);
  const [activeStep, setActiveStep] = useState(null);
  const [cardSelected, setCardSelected] = useState(null);
  const [emptyState, setEmptyState] = useState(false);
  const [resultsValidationStatus, setResultsValidationStatus] = useState(null);
  const [evaluationCurrentPage, setEvaluationCurrentPage] = useState(0);
  const [evaluationPageSize, setEvaluationPageSize] = useState(EVALUATION_DEFAULT_PAGINATION_PAGE_SIZE);
  const [evaluationResults, setEvaluationResults] = useState([]);
  const [originalEvaluationPrograms, setOriginalEvaluationPrograms] = useState(
    [],
  );
  const [originalEvaluationResults, setOriginalEvaluationResults] = useState(
    [],
  );
  const [assignedReviews, setAssignedReviews] = useState([]);
  const [openPanel, setOpenPanel] = useState(false);
  const [currentResult, setCurrentResult] = useState(null);
  const [programsWithoutScores, setProgramsWithoutScores] = useState([]);
  const [openDialogForMissingScores, setOpenDialogForMissingScores] =
    useState(false);
  const [resultsInfo, setResultsInfo] = useState(new Map());
  const [originalResultsInfo, setOriginalResultsInfo] = useState(new Map());
  const [reconciliationPrograms, setReconciliationPrograms] = useState([]);
  const [originalReconciliationPrograms, setOriginalReconciliationPrograms] = useState([]);
  const { user } = useUser();
  const { setLoading } = useContext(LoaderContext);
  const { nameConventions } = useNameConventions();
  const [state, dispatch] = useContext(Context);

  useLayoutEffect(() => {
    setOpenPanel(false);
    setCardSelected(null);
    setActiveStep(null);
    setCurrentResult(null);
    prepare();
  }, [user?.activeOrganization, user?.activeBudget, user?.activeCostCenters]);

  const prepare = async () => {
    setLoading(true);
    try {
      const [assignedReviewsResponse, scoredProgramsResponse] =
        await Promise.all([
          getAssignedPeerReviews(),
          getScoredServiceTypePrograms(),
        ]);

      let results = assignedReviewsResponse.data.map((result) =>
        appendProgramsToResult(result, scoredProgramsResponse.data, user),
      );

      if (assignedReviewsResponse.data.length) {
        setResultsValidationStatus(true);
      }

      setEvaluationResults(results);
      setOriginalEvaluationPrograms(
        structuredClone(scoredProgramsResponse.data),
      );
      setOriginalEvaluationResults(structuredClone(results));
      if (assignedReviewsResponse.data.length === 0) {
        setEmptyState(true);
      } else {
        setEmptyState(false);
        setAssignedReviews(assignedReviewsResponse.data);
      }
    } catch (error) {
      ToastComponent.present({
        message:
          messageManager(nameConventions).PEER_REVIEW.DEPARTMENT_PREPARE_ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  const saveEvaluationProgress = async (id, cancelFired) => {
    const { evaluationsToSave, programsWithoutScores } =
      mapEvaluationsForSaving(id, currentResult.programs, currentResult);
    if (programsWithoutScores.length !== 0) {
      setProgramsWithoutScores(programsWithoutScores);
      if (state.backButtonFired || cancelFired) {
        ToastComponent.present({
          message:
            messageManager().PEER_REVIEW.EVALUATION_STEP
              .SAVING_ERROR_FROM_UNSAVED_CHANGES_DIALOG,
        });
        cancelFired ?
          setCurrentResult(
            structuredClone(
              originalEvaluationResults.find(
                (result) => result.id === currentResult?.id,
              ),
            ),
          )
          : null;
        return;
      }
      setOpenDialogForMissingScores(true);
      dispatch({
        type: REDUCER_ACTION_TYPES.FIRE_UNSAVED_CHANGES_DIALOG,
        payload: true,
      });
      return;
    }
    if (evaluationsToSave && evaluationsToSave.length) {
      setLoading(true);
      try {
        await saveScores(evaluationsToSave, {
          disallowGlobalBadRequestPopups: true,
        });
        prepare();
        setOpenPanel(false);
        setCurrentResult(null);
        setCardSelected(null);
        dispatch({
          type: REDUCER_ACTION_TYPES.FIRE_UNSAVED_CHANGES_DIALOG,
          payload: false,
        });
        ToastComponent.present({
          message:
            messageManager(nameConventions).PEER_REVIEW.SAVE_PROGRESS_SUCCESS,
        });
      } catch (error) {
        ToastComponent.present({
          message:
            messageManager(nameConventions).PEER_REVIEW.EVALUATION_STEP
              .SAVE_PROGRESS_ERROR,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEvaluation = (type, programId, value, assessment, updatedProgram) => {
    const program = currentResult?.programs.find(item => item.id === programId);
    if (!program) return;
    const evaluatingProgram = updatedProgram || updateEvaluationProgram(program, type, value, assessment);
    dispatch({
      type: REDUCER_ACTION_TYPES.FIRE_UNSAVED_CHANGES_DIALOG,
      payload: true,
    });
    const isCurrentUsersScore = score =>
      score.programId === programId && score.lastUser === user.email;
    const existingScore = currentResult.peerScores.find(isCurrentUsersScore);
    const updatedScore = {
      ...(existingScore || {
        programId: evaluatingProgram.id,
        budgetId: user.activeBudget.id,
        lastUser: user.email,
        scoreOption: evaluatingProgram.scoreOptions.find(option => option.name === evaluatingProgram.peerScore),
      }),
      peerScore: evaluatingProgram.peerScore,
      peerComment: evaluatingProgram.peerComment,
      agreed: evaluatingProgram.agreed,
      originalPeerScore: '',
    };
    const updatedResult = {
      ...currentResult,
      programs: currentResult.programs.map(item => item.id === programId ? evaluatingProgram : item),
      peerScores: existingScore
        ? currentResult.peerScores.map(score => isCurrentUsersScore(score) ? updatedScore : score)
        : [...currentResult.peerScores, updatedScore],
    };
    setEvaluationResults(previous => previous.map(result => result.id === updatedResult.id ? updatedResult : result));
    setCurrentResult(updatedResult);
  };

  const restoreTeamReconciliationChanges = () => {
    setResultsInfo(structuredClone(originalResultsInfo));
    setReconciliationPrograms(structuredClone(originalReconciliationPrograms));
  };

  const saveReconciliationChanges = async () => {
    const scoresToSave = getTeamScoresToSave(resultsInfo, departmentReconciliation);
    setLoading(true);
    try {
      const response = await saveScores(scoresToSave, {
        disallowGlobalBadRequestPopups: true,
      });
      if (response.status === 200) {
        setOriginalResultsInfo(structuredClone(resultsInfo));
        setOriginalReconciliationPrograms(structuredClone(reconciliationPrograms));
        dispatch({
          type: REDUCER_ACTION_TYPES.FIRE_UNSAVED_CHANGES_DIALOG,
          payload: false,
        });
        ToastComponent.present({
          message:
            messageManager(nameConventions).PEER_REVIEW.SAVE_PROGRESS_SUCCESS,
        });
      }
    } catch (error) {
      ToastComponent.present({
        message:
          messageManager(nameConventions).PEER_REVIEW.SAVING_SCORES_ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  const restoreDepartmentReconciliationChanges = () => {
    setResultsInfo(structuredClone(originalResultsInfo));
    setReconciliationPrograms(structuredClone(originalReconciliationPrograms));
  };

  return (
    <PeerReviewContext.Provider
      value={{
        activeStep,
        setActiveStep,
        cardSelected,
        setCardSelected,
        emptyState,
        resultsValidationStatus,
        evaluationResults,
        setEvaluationResults,
        assignedReviews,
        openPanel,
        setOpenPanel,
        originalEvaluationPrograms,
        originalEvaluationResults,
        saveEvaluationProgress,
        currentResult,
        setCurrentResult,
        handleEvaluation,
        programsWithoutScores,
        openDialogForMissingScores,
        setOpenDialogForMissingScores,
        prepare,
        saveReconciliationChanges,
        restoreTeamReconciliationChanges,
        resultsInfo,
        setResultsInfo,
        originalResultsInfo,
        setOriginalResultsInfo,
        departmentReconciliation,
        setDepartmentReconciliation,
        restoreDepartmentReconciliationChanges,
        evaluationCurrentPage,
        setEvaluationCurrentPage,
        evaluationPageSize,
        setEvaluationPageSize,
        reconciliationPrograms,
        setReconciliationPrograms,
        setOriginalReconciliationPrograms
      }}
    >
      {children}
    </PeerReviewContext.Provider>
  );
};

PeerReviewContextProvider.propTypes = {
  children: PropTypes.node,
};

export default PeerReviewContextProvider;
export { PeerReviewContext };
